import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { subscriptionApi } from '../services/api'
import { Subscription, PricingPlan, TIER_FEATURES, TIER_DISPLAY_NAMES, TIER_MONTHLY_PRICES, SubscriptionTier } from '@treu/shared'
// import { LoadingSpinner } from '@treu/ui-components'
const LoadingSpinner = () => <div className="animate-pulse">Loading...</div>

// Pricing plans — tier name + price come from @treu/shared (single source of truth)
const PRICING_PLANS: PricingPlan[] = [
  {
    tier: 'basic',
    name: TIER_DISPLAY_NAMES.basic,
    price: { monthly: TIER_MONTHLY_PRICES.basic },
    description: 'Essential verification for everyday use',
    features: [
      '100 verifications/month',
      'AI-powered fact-checking',
      'Up to 4 trusted sources',
      'Mobile + web access'
    ],
    limits: {
      factChecks: '100/month',
      sources: TIER_FEATURES.basic.sources.length,
      communityAccess: TIER_FEATURES.basic.communityAccess,
      analytics: TIER_FEATURES.basic.analytics,
      extensionAccess: TIER_FEATURES.basic.extensionAccess
    }
  },
  {
    tier: 'premium',
    name: TIER_DISPLAY_NAMES.premium,
    price: { monthly: TIER_MONTHLY_PRICES.premium },
    description: 'Enhanced verification with priority features',
    features: [
      'Everything in Basic',
      '200 verifications/month',
      'Deeper analysis & nuanced responses using AI',
      'Up to 7 trusted sources',
      'Chrome extension access',
      'Analytics dashboard',
      'Community access (read-only)'
    ],
    limits: {
      factChecks: '200/month',
      sources: TIER_FEATURES.premium.sources.length,
      communityAccess: TIER_FEATURES.premium.communityAccess,
      analytics: TIER_FEATURES.premium.analytics,
      extensionAccess: TIER_FEATURES.premium.extensionAccess
    },
    popular: true
  },
  {
    tier: 'professional',
    name: TIER_DISPLAY_NAMES.professional,
    price: { monthly: TIER_MONTHLY_PRICES.professional },
    description: 'Unlimited verification for power users',
    features: [
      'Everything in Premium',
      'Unlimited verifications',
      'Complex claim analysis using advanced AI',
      '9+ trusted sources',
      'Full community access'
    ],
    limits: {
      factChecks: 'Unlimited',
      sources: TIER_FEATURES.professional.sources.length,
      communityAccess: TIER_FEATURES.professional.communityAccess,
      analytics: TIER_FEATURES.professional.analytics,
      extensionAccess: TIER_FEATURES.professional.extensionAccess
    }
  }
]

// Status pill styling per subscription status (token colors only)
const statusPillClasses = (status: Subscription['status']): string => {
  switch (status) {
    case 'active':
      return 'bg-accent-100 text-accent-600'
    case 'cancelled':
      return 'bg-danger-100 text-danger-500'
    case 'trialing':
      return 'bg-brand-100 text-brand-700'
    default:
      return 'bg-paper-100 text-ink-600'
  }
}

const SubscriptionManagement: React.FC = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [loading, setLoading] = useState(true)
  const [upgrading, setUpgrading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const location = useLocation()
  const choosePlanRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadSubscription()
    checkRedirectStatus()
  }, [])

  // `/profile#upgrade` means "land on the plan grid". The grid only exists once
  // the subscription has loaded (the component renders a spinner until then), so
  // the scroll has to wait for `loading` to flip rather than run on a timer —
  // a fixed delay in the parent page fired before this fetch finished when the
  // user arrived from another page, found nothing to scroll to, and gave up.
  useEffect(() => {
    if (loading || location.hash !== '#upgrade') return
    choosePlanRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' })
  }, [loading, location.hash])

  const checkRedirectStatus = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const subscriptionStatus = urlParams.get('subscription')

    if (subscriptionStatus === 'success') {
      setSuccessMessage('🎉 Subscription upgraded successfully! Your new plan is now active.')
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
      // Auto-hide success message after 10 seconds
      setTimeout(() => setSuccessMessage(null), 10000)
    } else if (subscriptionStatus === 'cancelled') {
      setError('Subscription upgrade was cancelled. You can try again anytime.')
      setTimeout(() => setError(null), 5000)
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }

  const loadSubscription = async () => {
    try {
      const sub = await subscriptionApi.getSubscription()
      setSubscription(sub)
    } catch (err) {
      console.error('Failed to load subscription:', err)
      // For now, default to free user since backend isn't fully implemented
      setSubscription({
        tier: 'free',
        status: 'active',
        monthlyChecksUsed: 0,
        monthlyChecksLimit: 10
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUpgrade = async (tier: SubscriptionTier) => {
    setUpgrading(tier)
    setError(null)

    const hasExistingSubscription = subscription &&
      subscription.tier !== 'free' &&
      subscription.status === 'active'

    try {
      if (hasExistingSubscription) {
        // Use update endpoint for existing subscriptions (upgrades and downgrades)
        const response = await subscriptionApi.updateSubscription(tier)
        setSuccessMessage(response.message || `Successfully changed to ${tier}`)
        await loadSubscription()
        setUpgrading(null)
      } else {
        // Use checkout for new subscriptions
        const { checkoutUrl } = await subscriptionApi.createCheckout(tier)
        window.location.href = checkoutUrl
      }
    } catch (err: unknown) {
      console.error('Subscription change failed:', err)
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null
      setError(errorMessage || 'Failed to process subscription change')
      setUpgrading(null)
    }
  }

  const handleDowngrade = async (tier: SubscriptionTier) => {
    const confirmed = confirm(
      `Are you sure you want to downgrade to ${tier}?\n\n` +
      `Your current plan features will remain active until the end of your billing period, ` +
      `then you'll be switched to the ${tier} plan.`
    )
    if (confirmed) {
      await handleUpgrade(tier)
    }
  }

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
      return
    }

    try {
      await subscriptionApi.cancelSubscription()
      await loadSubscription() // Reload to show updated status
      setError(null)
    } catch (err) {
      console.error('Failed to cancel subscription:', err)
      setError('Failed to cancel subscription')
    }
  }

  const handleManageSubscription = async () => {
    try {
      const { customerPortalUrl } = await subscriptionApi.getCustomerPortal()
      window.open(customerPortalUrl, '_blank')
    } catch (err) {
      console.error('Failed to open customer portal:', err)
      setError('Failed to open subscription management')
    }
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-paper-200 bg-white p-6 shadow-card sm:p-8">
        <div className="mb-4 font-display text-xs font-medium uppercase tracking-[0.25em] text-accent-600">
          Account Management
        </div>
        <div className="flex min-h-[200px] items-center justify-center text-ink-400">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-paper-200 bg-white p-6 shadow-card sm:p-8">
      <div className="mb-4 font-display text-xs font-medium uppercase tracking-[0.25em] text-accent-600">
        Account Management
      </div>

      {error && (
        <div className="mb-6 rounded-sm border border-danger-500/30 bg-danger-100 p-4 text-sm text-danger-500">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-6 rounded-sm border border-accent-300 bg-accent-100 p-4 text-sm text-accent-600">
          {successMessage}
        </div>
      )}

      {/* Current Subscription Status */}
      {subscription && (
        <div className="mb-8 rounded-lg border border-paper-200 bg-paper-50 p-6">
          <h4 className="mb-4 text-lg font-semibold text-ink-900">Current Plan</h4>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="mb-2 text-sm text-ink-400">Plan</p>
              <p className="text-lg font-semibold capitalize text-ink-900">{subscription.tier}</p>
            </div>
            <div>
              <p className="mb-2 text-sm text-ink-400">Status</p>
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${statusPillClasses(subscription.status)}`}>
                {subscription.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            {subscription.tier === 'free' && subscription.monthlyChecksLimit && (
              <div>
                <p className="mb-2 text-sm text-ink-400">Monthly Usage</p>
                <p className="mb-1 text-lg font-semibold text-ink-900">
                  {subscription.monthlyChecksUsed ?? 0} / {subscription.monthlyChecksLimit} checks used
                </p>
                <p className="text-sm text-ink-400">
                  {subscription.monthlyChecksLimit - (subscription.monthlyChecksUsed ?? 0)} remaining
                </p>
              </div>
            )}
            {subscription.currentPeriodEnd && subscription.tier !== 'free' && (
              <div>
                <p className="mb-2 text-sm text-ink-400">
                  {subscription.status === 'cancelled' ? 'Access Until' : 'Next Billing'}
                </p>
                <p className="text-lg font-semibold text-ink-900">
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {subscription.tier !== 'free' && (
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={handleManageSubscription}
                className="min-h-[44px] w-full rounded-md border border-paper-200 bg-white px-5 py-3 text-sm font-medium text-ink-700 transition-colors hover:bg-paper-100 sm:w-auto"
              >
                Manage Billing
              </button>
              {subscription.status === 'active' && (
                <button
                  onClick={handleCancelSubscription}
                  className="min-h-[44px] w-full rounded-md border border-danger-100 bg-transparent px-5 py-3 text-sm font-medium text-danger-500 transition-colors hover:bg-danger-100/50 sm:w-auto"
                >
                  Cancel Plan
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Available Plans */}
      <div id="choose-plan" ref={choosePlanRef} className="mb-8 scroll-mt-[100px] text-center">
        <h4 className="mb-4 font-display text-3xl font-semibold text-ink-900 sm:text-4xl">
          {subscription?.tier === 'free' ? 'Choose Your Plan' : 'Upgrade Your Plan'}
        </h4>
        <p className="mx-auto max-w-[600px] text-base leading-relaxed text-ink-600 sm:text-lg">
          Upgrade your Treu experience with unlimited verifications and advanced features
        </p>
      </div>
      <div>
        <div className="mb-8 grid grid-cols-1 items-stretch gap-6 md:grid-cols-2 xl:grid-cols-3">
          {PRICING_PLANS.map((plan) => {
            const isCurrentPlan = subscription?.tier === plan.tier && subscription.status === 'active'
            // Only show downgrade if subscription is active (not cancelled)
            const isDowngrade = subscription &&
              subscription.tier !== 'free' &&
              subscription.status === 'active' &&
              PRICING_PLANS.findIndex(p => p.tier === subscription.tier) >
              PRICING_PLANS.findIndex(p => p.tier === plan.tier)

            return (
              <div
                key={plan.tier}
                className={`relative flex flex-col rounded-lg bg-white p-6 shadow-card sm:p-8 ${
                  plan.popular ? 'border-2 border-accent-500' : 'border border-paper-200'
                }`}
              >
                {plan.popular && !isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-accent-500 px-4 py-1 text-center text-xs font-semibold text-white">
                    Most Popular
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="mb-4 rounded-sm bg-accent-100 py-1 text-center text-xs font-medium text-accent-600">
                    Current Plan
                  </div>
                )}

                <div className="mb-8 text-center">
                  <h5 className={`mb-4 font-display text-xl font-semibold text-ink-900 ${plan.popular ? 'mt-4' : ''}`}>
                    {plan.name}
                  </h5>

                  <div className="mb-4">
                    <span className="font-display text-4xl font-semibold text-ink-900 sm:text-5xl">${plan.price.monthly}</span>
                    <span className="ml-1 text-base text-ink-400">/month</span>
                  </div>

                  <p className="text-sm leading-snug text-ink-600">{plan.description}</p>
                </div>

                <ul className="mb-8 grow list-none p-0">
                  {plan.features.slice(0, 4).map((feature, index) => (
                    <li key={index} className="mb-3 flex items-center text-sm text-ink-600">
                      <svg className="mr-3 h-[18px] w-[18px] shrink-0 text-accent-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                  {plan.features.length > 4 && (
                    <li className="pl-[30px] text-xs italic text-ink-400">
                      +{plan.features.length - 4} more features
                    </li>
                  )}
                </ul>

                <button
                  onClick={() => isDowngrade ? handleDowngrade(plan.tier) : handleUpgrade(plan.tier)}
                  disabled={upgrading === plan.tier || isCurrentPlan}
                  className={`min-h-[48px] w-full rounded-md px-6 py-3 text-sm font-semibold transition-colors ${
                    isCurrentPlan
                      ? 'cursor-not-allowed bg-accent-100 text-accent-600'
                      : isDowngrade
                      ? 'border-2 border-brand-950 bg-transparent text-brand-950 hover:bg-brand-50'
                      : upgrading === plan.tier
                      ? 'cursor-not-allowed bg-accent-500 text-white opacity-60'
                      : 'bg-accent-500 text-white hover:bg-accent-600'
                  }`}
                >
                  {upgrading === plan.tier ? (
                    'Processing...'
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : isDowngrade ? (
                    `Downgrade to ${plan.name}`
                  ) : (
                    `Upgrade to ${plan.name}`
                  )}
                </button>
              </div>
            )
          })}
        </div>
      </div>

    </div>
  )
}

export default SubscriptionManagement
